import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tarjeta } from "@/components/ConfigComunes";
import { useHikCentral } from "@/hooks/useConfig";
import type { HikCentralAgencia } from "@/api/types";

const VEREDICTO: Record<
  HikCentralAgencia["veredicto"],
  { texto: string; variant: "destructive" | "secondary" | "outline" }
> = {
  // Lleva el umbral completo con TODOS sus equipos abajo: esto si es incidente.
  caida: { texto: "Caída", variant: "destructive" },
  // Abajo ahora, pero sin evidencia de que lleve el tiempo minimo. El 31-ago
  // dos agencias se vieron 100% caidas ~1 h por un reinicio de router y se
  // recuperaron solas: pintarlas igual que una caida real seria mentir.
  transitorio: { texto: "Transitorio", variant: "secondary" },
  ok: { texto: "OK", variant: "outline" },
};

export function HikCentralPanel() {
  const { data, isLoading, isError } = useHikCentral();

  if (isLoading) {
    return <p className="text-sm text-muted-foreground">Cargando HikCentral…</p>;
  }
  if (isError || !data) {
    return (
      <p className="text-sm text-destructive">
        No se pudo consultar HikCentral.
      </p>
    );
  }

  if (data._status === "sin_configurar") {
    return (
      <div className="rounded-md border border-dashed p-4 text-sm text-muted-foreground">
        {data.mensaje}
      </div>
    );
  }

  // Que AROC no pueda leer la plataforma NO es que se cayeran los 400+ equipos.
  // Se dice con esas palabras para que nadie lo lea como una catastrofe.
  if (data._status === "sin_datos") {
    return (
      <div className="rounded-md border border-amber-500/50 bg-amber-500/5 p-4 text-sm">
        <p className="font-medium">Sin datos de HikCentral</p>
        <p className="text-muted-foreground">{data.mensaje}</p>
        {data.motivo && (
          <p className="mt-1 text-xs text-muted-foreground">Motivo: {data.motivo}</p>
        )}
        {data.ultima_lectura && (
          <p className="text-xs text-muted-foreground">
            Última lectura: {new Date(data.ultima_lectura).toLocaleString()}
          </p>
        )}
      </div>
    );
  }

  const t = data.totales;
  const r = data.resumen_agencias;
  const agencias = data.agencias ?? [];
  const firmware = Object.entries(data.firmware ?? {});

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
        {/* Cobertura primera, como en las demás pantallas. Aquí NO es
            cumplimiento de golden —HikCentral mide disponibilidad—, así que
            cuenta EN LÍNEA sobre en alcance, no cumplen sobre leídos. */}
        <Tarjeta
          titulo="Cobertura"
          valor={`${
            t?.en_alcance ? Math.round((100 * (t.en_linea ?? 0)) / t.en_alcance) : 0
          }%`}
          nota={`${t?.en_linea ?? 0} de ${t?.en_alcance ?? 0} en línea`}
          alerta={!!t?.en_alcance && (t.en_linea ?? 0) / t.en_alcance < 0.8}
        />
        <Tarjeta titulo="Equipos en alcance" valor={t?.en_alcance ?? 0} />
        <Tarjeta titulo="En línea" valor={t?.en_linea ?? 0} />
        <Tarjeta
          titulo="Caídos"
          valor={t?.caidos ?? 0}
          alerta={(t?.caidos ?? 0) > 0}
        />
        <Tarjeta
          titulo="Agencias caídas"
          valor={r?.caida ?? 0}
          nota={`${r?.transitorio ?? 0} transitorias`}
          alerta={(r?.caida ?? 0) > 0}
        />
        <Tarjeta
          titulo="Fuera de golden"
          valor={data.firmware_fuera_de_golden ?? 0}
          nota="firmware"
        />
      </div>

      <p className="text-xs text-muted-foreground">
        {data.plataforma?.produceName} {data.plataforma?.softVersion} ·{" "}
        {t?.fuera_de_alcance ?? 0} equipos fuera de alcance (móviles y otros
        clientes, no son flota Holanda) · una agencia se declara caída tras{" "}
        {data.minutos_para_caida} min completos abajo ({data.lecturas_disponibles}{" "}
        lecturas disponibles) ·{" "}
        {data.capturado_en && new Date(data.capturado_en).toLocaleString()}
      </p>

      <div>
        <h3 className="mb-2 text-sm font-medium">Estado por agencia</h3>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Agencia</TableHead>
              <TableHead>Estado</TableHead>
              <TableHead className="text-right">Equipos</TableHead>
              <TableHead className="text-right">En línea</TableHead>
              <TableHead className="text-right">Caídos</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {agencias.map((a) => {
              const v = VEREDICTO[a.veredicto];
              return (
                <TableRow key={a.agencia}>
                  <TableCell className="font-medium">{a.agencia}</TableCell>
                  <TableCell>
                    <Badge variant={v.variant}>{v.texto}</Badge>
                  </TableCell>
                  <TableCell className="text-right">{a.total}</TableCell>
                  <TableCell className="text-right">{a.en_linea}</TableCell>
                  <TableCell className="text-right">{a.caidos}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      <div>
        <h3 className="mb-2 text-sm font-medium">
          Firmware por modelo — el golden es la versión mayoritaria
        </h3>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Modelo</TableHead>
              <TableHead>Golden</TableHead>
              <TableHead className="text-right">En golden</TableHead>
              <TableHead className="text-right">Fuera</TableHead>
              <TableHead>Versiones</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {firmware.map(([modelo, m]) => (
              <TableRow key={modelo}>
                <TableCell className="font-medium">{modelo}</TableCell>
                <TableCell>
                  <code className="text-xs">{m.golden}</code>
                </TableCell>
                <TableCell className="text-right">{m.en_golden}</TableCell>
                <TableCell
                  className={
                    m.fuera > 0 ? "text-right font-medium text-destructive" : "text-right"
                  }
                >
                  {m.fuera}
                </TableCell>
                <TableCell className="text-xs text-muted-foreground">
                  {Object.entries(m.versiones)
                    .map(([v, n]) => `${v} (${n})`)
                    .join(" · ")}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
